import { Locale, NotificationTypeV3 } from "./enums";

export type UserV3 = {
    id: number;
    uuid: string;
    nanoID: string;
    email: string;
    firstName: string;
    lastName: string;
    stripeID: string;
    accountID: number;
    __roles: string;
    roles: string;
    esims: EsimV3[];
    payments: PaymentV3[];
    notifications: UserNotificationV3[];
    deletedAt: Date | null;
    orders: OrderV3[];
    currentLocation: string;
    deviceInfo?: string;
    username?: string;
    createdAt?: string;
    updatedAt?: string;
}

export type EsimV3 = {
    iccid: string;
    nanoID: string;
    matchingId: string;
    smdpPlus: string;
    msisdn: string;
    status: EsimStatusV3;
    userId: number;
    accountId: number;
    providerID: number;
    provider: ProviderV3;
    providerConfigVersion: number;
    qrCodeLink: string;
    subscriptions: SubscriptionV3[];
    createdAt: string;
    updatedAt: string;
}

export type OrderV3 = {
    nanoID: string;
    status: OrderStatusV3;
    paymentID: number;
    accountID: number;
    userID: number;
    subscription: SubscriptionV3;
    providerPayload: string;
    providerOrderID: string;
    type: OrderTypesV3;
    notifyRecipient: boolean;
    failedReason: string;
    deletedAt: Date | null;
}

export type UserNotificationV3 = {
    nanoID: string;
    userID: number;
    templateID: number;
    status: string;
    deletedAt: Date | null;
}

export type PaymentV3 = {
    nanoID: string;
    status: PaymentStatusV3;
    stripeIntentID: string;
    invoiceID: number;
    userID: number;
    accountID: number;
    deletedAt: Date | null;
}

export enum EsimStatusV3 {
  PendingInstall = "PENDING_INSTALL",
  Installed = 'Installed',
  Online = "ONLINE",
  Uninstalled = "UNINSTALLED",
  Released = 'Released',
  Unavailable = 'Unavailable',
  Downloaded = 'Downloaded',
}

export type ProviderV3 = {
    nanoID: string;
    name: string;
    logoURL: string;
    config: ProviderConfigV3;
    plans: PlanV3[];
    status: ConnectorStateV3;
    lastCatalogRefresh: Date | null;
    isDemoProvider: boolean;
    isZIMBranded: boolean;
    desplayName: string;
}

export type ProviderConfigV3 = {
    smdpPlus: string;
    orderConfig: string;
    requiredOrderAttrsStr: string;
    perSubscriptionAddFeeCentsUSD: number;
    terms: { en: string; fr: string };
    providerId: number;
}

export type SubscriptionV3 = {
    nanoID: string;
    deactivationReason: string;
    activityBuckets: ActivityBucketV3[];
    status: SubscriptionStatusV3;
    planID: number;
    plan: PlanV3;
    orderID: number;
    order: OrderV3;
    userID: number;
    esimID: number;
    accountID: number;
    providerSubscriptionID: string;
    expirationDate: Date | null;
    activity?: ActivityV3;
    topupRequested: boolean;
    createdAt?: string;
}

export type ActivityV3 = {
    currentBalanceBytes: number;
    initialBalanceBytes: number;
    lastUpdatedAt: string;
    spentBalanceBytes: number;
}

export enum OrderStatusV3 {
  PendingApproval = "PENDING_APPROVAL",
  PendingPayment = "PENDING_PAYMENT",
  Processing = "PROCESSING",
  Fulfilled = "FULFILLED",
  Cancelled = "CANCELLED",
  Failed = "FAILED",
  InBasket = "IN_BASKET"
}

export enum SubscriptionStatusV3 {
  Active = "ACTIVE",
  Expired = "EXPIRED",
  Cancelled = "CANCELLED",
  Depleted = 'DEPLETED'
}

export type ActivityBucketV3 = {
    periodicity: string;
    metric: string
    value: string;
    valueType: string;
    accountID: number;
    subscriptionID: number;
    userId: number;
}

export type PlanV3 = {
    nanoID: string;
    stipeId: string;
    providerPlanID: string;
    dataUnit: string;
    dataValue: number;
    callUnit: string;
    callValue: number;
    smsUnit: string;
    smsValue: number;
    durationUnit: string;
    durationValue: number;
    networkProviders: NetworkProviderV3[];
    providerID: number;
    provider: ProviderV3;
    destinationID: number;
    destination: DestinationV3;
    _destination?: Partial<DestinationV3>;
    regionID: number;
    region: RegionV3;
    features: PlanFeaturesV3[];
    providerQuote: ProviderQuoteV3;
    prices: PriceV3[];
    orders: OrderV3[];
    throttleProfileID: number;
    domain: PlanDomainV3;
    name: string;
    type: PlanTypeV3;
    catergory: PlanCategoryV3;
    scope: PlanScopeV3;
    isArchived: boolean;
}

export type DestinationV3 = {
  name: string;
  isoAlpha3: string;
  regions: RegionV3[];
  plans: PlanV3[];
}

export enum OrderTypesV3 {
  OneOff = "ONE_OFF",
  TopUp = "TOP_UP",
  Recurring = "RECURRING"
}

export enum PaymentStatusV3 {
  Pending = 'PENDING',
  Received = 'RECEIVED',
  Returned = 'RETURNED',
  Failed = 'FAILED'
}

export enum ConnectorStateV3 {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  TEMPORARY_FAILURE = 'TEMPORARY_FAILURE',
  FORCE_OFFLINE = 'FORCE_OFFLINE'
}

export type NetworkProviderV3 = {
  name: string;
  quality: string;
}

export enum PlanFeaturesV3 {
  '3G' = '3G',
  '4G' = '4G',
  '5G' = '5G',
  'HOTSPOT' = 'HOTSPOT',
  'TOP_UP' = 'TOP_UP',
}

export type ProviderQuoteV3 = {
  currency: string;
  value: number;
  quotedAt: Date;
  marginPercentage: number;
  region: RegionV3;
  planId: number;
}

export type PriceV3 = {
  currencyISO: string;
  value: number;
  minorUnitsExponent: number;
  planID: number;
}

export enum PlanDomainV3 {
  B2B = "B2B",
  B2C = "B2C",
}

export enum PlanTypeV3 {
  DATA = 'DATA',
  VOICE_AND_DATA = 'VOICE_AND_DATA',
}

export enum PlanCategoryV3 {
  UNLIMITED = 'UNLIMITED',
  REGULAR = 'REGULAR',
  MIXED = 'MIXED'
}

export type RegionV3 = {
  name: RegionEnumV3;
  abbreviation: string;
  plans: PlanV3[];
}

export enum RegionEnumV3 {
  AMERICAS = 'Americas',
  CARIBBEAN = 'Caribbean',
  ASIA = 'Asia',
  SOUTHERN_ASIA = 'Southern Asia',
  AFRICA = 'Africa',
  MIDDLE_AFRICA = 'Middle Africa',
  EUROPE = 'Europe',
  NORTHERN_EUROPE = 'Northern Europe',
  SOUTHEAST_EUROPE = 'Southeast Europe',
  SOUTHERN_EUROPE = 'Southern Europe',
  WESTERN_ASIA = 'Western Asia',
  SOUTH_AMERICA = 'South America',
  OCEANIA = 'Oceania',
  POLYNESIA = 'Polynesia',
  ANTARCTIC = 'Antarctic',
  AUSTRALIA_AND_NEW_ZEALAND = 'Australia and New Zealand',
  CENTRAL_EUROPE = 'Central Europe',
  EASTERN_AFRICA = 'Eastern Africa',
  WESTERN_EUROPE = 'Western Europe',
  WESTERN_AFRICA = 'Western Africa',
  EASTERN_EUROPE = 'Eastern Europe',
  CENTRAL_AMERICA = 'Central America',
  NORTH_AMERICA = 'North America',
  SOUTH_EASTERN_ASIA = 'South-Eastern Asia',
  SOUTHERN_AFRICA = 'Southern Africa',
  EASTERN_ASIA = 'Eastern Asia',
  NORTHERN_AFRICA = 'Northern Africa',
  MELANESIA = 'Melanesia',
  MICRONESIA = 'Micronesia',
  CENTRAL_ASIA = 'Central Asia',
  GLOBAL = 'Global',
}

export enum PlanScopeV3 {
  LOCAL = 'LOCAL',
  REGIONAL = 'REGIONAL',
  GLOBAL = 'GLOBAL'
}

export type NotificationV3 = {
    type: NotificationTypeV3;
    lang: Locale;
    userID: number;
    payload: {
      // title?: string;
      // body?: string;
      discountPercent?: number;
      promoCode?: string;
      name?: string;
    }
}

export type B2bAccount = {
  nanoId: string;
  deletedAt: Date | null;
  type: AccountType;
  name: string;
  ownerID: number;
  owner: UserV3;
  users: UserV3[];
  orders: OrderV3[];
  payments: PaymentV3[];
  subscriptions: SubscriptionV3[];
  paymentMode: AccountPaymentMode;
  stripeID: string;
}

export enum AccountPaymentMode {
  CARD = 'CARD',
  ADVANCE_PAY_ONLY = 'ADVANCE_PAY_ONLY',
  ADVANCE_PAY_AND_CARD = 'ADVANCE_PAY_AND_CARD',
}

export enum AccountType {
  ORGANISATION = 'ORGANISATION',
  TEAM = 'TEAM',
  FAMILY = 'FAMILY',
  INDIVIDUAL = 'INDIVIDUAL',
}
